import { createRouter, createWebHistory } from 'vue-router';
import { useUsers } from '@/features/users/controller';
import { useToast } from 'primevue/usetoast';

const getRouteFromAbility = (ability: string | null) => {
  switch (ability) {
    case 'veterinary':
      return 'veterinary';
    case 'watcher':
      return 'watcher';
    case 'farrier':
      return 'farrier';
    case 'medical':
      return 'medical';
    case 'ticketing':
      return 'ticketing';
    default:
      return null;
  }
};

const clearSession = (userStore: ReturnType<typeof useUsers>) => {
  userStore.loggedUser = null;
  userStore.userToken = null;
  userStore.userAbility = null;
};

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/connexion',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/access/:app/:token',
    name: 'token-access',
    component: () => import('@/views/TokenAccessView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/veterinaire',
    name: 'veterinary',
    component: () => import('@/views/veterinaries/VeterinaryView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/veterinaire/nouveau',
    name: 'veterinary-create',
    component: () => import('@/views/veterinaries/VeterinaryCreateView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/veterinaire/confirmation',
    name: 'veterinary-confirmation',
    component: () => import('@/views/veterinaries/VeterinaryConfirmationView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/veterinaire/:id',
    name: 'veterinary-detail',
    component: () => import('@/views/veterinaries/VeterinaryDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/guetteur',
    name: 'watcher',
    component: () => import('@/views/watchers/WatcherView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/guetteur/:id',
    name: 'watcher-detail',
    component: () => import('@/views/watchers/WatcherDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/guetteur/nouveau/:raceId',
    name: 'watcher-create',
    component: () => import('@/views/watchers/WatcherFormView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/guetteur/:raceId/incident/:incidentId',
    name: 'watcher-incident-detail',
    component: () => import('@/views/watchers/WatcherIncidentDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/guetteur/:raceId/incident/:incidentId/modifier',
    name: 'watcher-edit',
    component: () => import('@/views/watchers/WatcherFormView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/marechal',
    name: 'farrier',
    component: () => import('@/views/farriers/FarrierView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/marechal/nouveau',
    name: 'farrier-create',
    component: () => import('@/views/farriers/FarrierCreateView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/marechal/:id',
    name: 'farrier-detail',
    component: () => import('@/views/farriers/FarrierDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical',
    name: 'medical',
    component: () => import('@/views/doctors/DoctorView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/:id',
    name: 'medical-detail',
    component: () => import('@/views/doctors/DoctorDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/billetterie',
    name: 'ticketing',
    component: () => import('@/views/ticketing/TicketingView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/billetterie/inscrits',
    name: 'ticketing-list',
    component: () => import('@/views/ticketing/TicketingListView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/billetterie/inscrit/:id',
    name: 'ticketing-detail',
    component: () => import('@/views/ticketing/TicketingDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/prelevement/:id',
    name: 'medical-sampling-detail',
    component: () => import('@/views/doctors/DoctorSamplingDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/incident-jockey/nouveau',
    name: 'medical-incident-jockey-create',
    component: () => import('@/views/doctors/DoctorIncidentJockeyCreateView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/incident-professionnel/nouveau',
    name: 'medical-incident-professional-create',
    component: () => import('@/views/doctors/DoctorIncidentProfessionalCreateView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/incident-professionnel/:id',
    name: 'medical-incident-professional-detail',
    component: () => import('@/views/doctors/DoctorProfessionalDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/incident-public/nouveau',
    name: 'medical-incident-public-create',
    component: () => import('@/views/doctors/DoctorIncidentPublicCreateView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/incident-public/:id',
    name: 'medical-incident-public-detail',
    component: () => import('@/views/doctors/DoctorPublicDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/prelevement-jockey/nouveau',
    name: 'medical-sampling-jockey-create',
    component: () => import('@/views/doctors/DoctorSamplingJockeyCreateView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/prelevement-jockey/:id/modifier',
    name: 'medical-sampling-jockey-edit',
    component: () => import('@/views/doctors/DoctorSamplingJockeyEditView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/alcoolemie/nouveau',
    name: 'medical-alcohol-test-create',
    component: () => import('@/views/doctors/DoctorAlcoholTestCreateView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/alcoolemie/:id',
    name: 'medical-alcohol-test-detail',
    component: () => import('@/views/doctors/DoctorAlcoholTestDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/medical/alcoolemie/:id/modifier',
    name: 'medical-alcohol-test-edit',
    component: () => import('@/views/doctors/DoctorAlcoholTestEditView.vue'),
    meta: { requiresAuth: true }
  },
  // redirect le reste
  {
    path: '/:pathMatch(.*)*',
    redirect: "/",
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});


router.beforeEach(async (to, from, next) => {
  const $userStore = useUsers();
  const toast = useToast();
  const appRouteName = getRouteFromAbility($userStore.userAbility);

  if (to.name === 'home') {
    if (appRouteName) {
      next({ name: appRouteName });
      return;
    }

    if ($userStore.userToken) {
      clearSession($userStore);
    }

    next({ name: 'login' });
    return;
  }

  if (to.name === 'login' && $userStore.userToken) {
    if (appRouteName) {
      next({ name: appRouteName });
      return;
    }

    clearSession($userStore);
    next({ name: 'login' });
    return;
  }

  if ($userStore.userToken && !appRouteName && to.name !== 'token-access') {
    clearSession($userStore);
  }

  // Check if need to be authenticated
  if (to.meta.requiresAuth && !$userStore.userToken && to.name !== 'login' && to.name !== 'token-access') {
    toast.add({ severity: 'error', summary: "Une erreur s'est produite", detail: 'Vous devez être connecté pour accéder à cette page', life: 3000 });
    next({ name: 'login' });
    return;
  }

  next();
});

export default router;